import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, FormControlLabel, Stack, Switch } from "@mui/material";
import { useMandatorVisibility } from "@helpers";
import {
  InvestmentListView,
  InvestmentSortField,
} from "@features/investments/investment/components/InvestmentTable/mobile";
import { SettingsSectionTitle, SortSelect } from "@components/MobileList";
import { ShowHistoric } from "../ShowHistoric";
import {
  AssetClassSetting,
  useShowsAssetClassSetting,
} from "./AssetClassSetting";
import { MetricOrderList } from "./MetricOrderList";
import { useInvestmentSortOptions } from "./useInvestmentSortOptions";

interface Props {
  view: InvestmentListView;
  showHistoric: boolean;
  onShowHistoricChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // Page specific controls, e.g. the currency toggle on the investments page.
  children?: ReactNode;
}

export const InvestmentListSettings = ({
  view,
  showHistoric,
  onShowHistoricChange,
  children,
}: Props) => {
  const { t } = useTranslation();
  const isHistoricVisible = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });

  const sortOptions = useInvestmentSortOptions(view);
  const showsAssetClass = useShowsAssetClassSetting();

  const fieldLabel = (field: InvestmentSortField) =>
    t(`domains.investments.investmentTable.mobile.fields.${field}`);

  return (
    <Stack
      divider={<Divider />}
      spacing={2}
      data-testid="investment-list-settings"
    >
      <Stack spacing={4} px={4} py={2}>
        {showsAssetClass && <AssetClassSetting />}
        <SortSelect
          label={t("domains.investments.investmentTable.mobile.sortBy")}
          options={sortOptions}
          field={view.sortField}
          direction={view.sortDirection}
          onChange={view.setSort}
        />
      </Stack>

      <Box py={2}>
        <SettingsSectionTitle>
          {t("domains.investments.investmentTable.mobile.chipsSection")}
        </SettingsSectionTitle>
        <MetricOrderList view={view} fieldLabel={fieldLabel} />
        <Box px={4} pt={2}>
          <FormControlLabel
            control={
              <Switch
                checked={view.showLabels}
                onChange={(event) => view.setShowLabels(event.target.checked)}
                size="medium"
                name="show-chip-labels-switch"
                color="primary"
              />
            }
            sx={{ m: 0 }}
            label={t(
              "domains.investments.investmentTable.mobile.showChipLabels"
            )}
          />
        </Box>
      </Box>

      {(children || isHistoricVisible) && (
        <Box py={2}>
          <SettingsSectionTitle>
            {t("domains.investments.investmentTable.mobile.view")}
          </SettingsSectionTitle>
          <Stack spacing={3} px={4} alignItems="flex-start">
            {children}
            {isHistoricVisible && (
              // ShowHistoric centres itself in flex layouts; the block wrapper
              // keeps it aligned with the other controls.
              <Box>
                <ShowHistoric
                  checked={showHistoric}
                  onChange={onShowHistoricChange}
                />
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
